import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Calendar as CalendarIcon, ArrowUpRight, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tripTypes = ["Family", "Friends", "Romantic Getaway", "Anniversary", "Honeymoon", "Milestone Birthday", "Wedding"];
const budgets = ["Under $3,000", "$3,000 – $6,000", "$6,000 – $10,000", "$10,000 – $20,000", "$20,000+"];

export default function InquiryForm() {
  const [destinations, setDestinations] = useState([]);
  const [start, setStart] = useState();
  const [end, setEnd] = useState();
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", destination: "", trip_type: "", guests: 2, budget: "", message: "",
  });

  useEffect(() => {
    axios.get(`${API}/destinations`).then((r) => setDestinations(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.destination || !form.trip_type || !form.budget || !start || !end) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/inquiries`, {
        ...form,
        guests: Number(form.guests),
        start_date: format(start, "yyyy-MM-dd"),
        end_date: format(end, "yyyy-MM-dd"),
      });
      setSuccess(res.data);
      toast.success("Inquiry received. We'll be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const startDeposit = async (inquiryId, email) => {
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/payments/checkout`, {
        payment_type: "deposit",
        package_id: "deposit_standard",
        origin_url: origin,
        inquiry_id: inquiryId,
        customer_email: email,
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error("Could not start checkout.");
    }
  };

  if (success) {
    return (
      <section id="inquiry" className="relative bg-[#F9F6F0] py-24 md:py-40 z-[2]" data-testid="inquiry-success-section">
        <div className="max-w-[900px] mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}
          >
            <div className="mx-auto w-14 h-14 rounded-full bg-[#C5A059]/15 flex items-center justify-center mb-8">
              <Check size={22} strokeWidth={1.5} className="text-[#C5A059]" />
            </div>
            <p className="eyebrow text-[#C5A059] mb-6">Inquiry Received</p>
            <h2 className="font-serif-editorial text-4xl md:text-6xl leading-[1] tracking-tight mb-8">
              Thank you, <span className="italic">{success.full_name.split(" ")[0]}</span>.
            </h2>
            <p className="text-[#1A1A1A]/75 max-w-xl mx-auto text-lg leading-relaxed mb-12">
              A concierge will reply within 24 to 72 hours with a tailored proposal. To secure your dates now, place a non-refundable $500 deposit — applied to your final trip. All payments are subject to the resort&rsquo;s cancellation policy.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button onClick={() => startDeposit(success.id, success.email)} className="ee-btn-primary" data-testid="btn-place-deposit">
                Place $500 Deposit <ArrowUpRight size={14} strokeWidth={1.5} />
              </button>
              <a href="#top" className="ee-btn-ghost" data-testid="btn-back-home">Back to top</a>
            </div>
            <p className="mt-10 text-xs uppercase tracking-[0.22em] text-[#5C5C5C]">
              Reference · {success.id.slice(0, 8)}
            </p>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="inquiry" className="relative bg-[#F9F6F0] py-24 md:py-40 z-[2]" data-testid="inquiry-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-6 md:gap-16">
          <div className="col-span-12 md:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-8 bg-[#C5A059]" />
              <span className="eyebrow text-[#C5A059]">Chapter · 04 · Begin</span>
            </div>
            <h2 className="font-serif-editorial text-5xl md:text-7xl leading-[0.95] tracking-tight mb-8">
              Let&rsquo;s start<br /><span className="italic">the escape.</span>
            </h2>
            <p className="text-[#1A1A1A]/75 leading-relaxed max-w-md mb-10">
              Share a few essentials. We reply within 24 to 72 hours to your request &mdash; and the planning begins.
            </p>
            <div className="space-y-4 text-sm text-[#1A1A1A]/70 border-t border-black/10 pt-8">
              <p><span className="eyebrow text-[#C5A059] mr-3">Included</span> All resort perks & concierge planning</p>
              <p><span className="eyebrow text-[#C5A059] mr-3">Not Included</span> Flights & transportation (recommendations provided)</p>
              <p><span className="eyebrow text-[#C5A059] mr-3">Deposit</span> $500 non-refundable · applied to final trip. Payments subject to resort cancellation policy.</p>
            </div>
          </div>

          <form onSubmit={submit} className="col-span-12 md:col-span-7 grid grid-cols-2 gap-x-6 gap-y-2" data-testid="inquiry-form">
            <div className="col-span-2 sm:col-span-1">
              <label className="eyebrow text-[#5C5C5C]">Full Name *</label>
              <input className="ee-input" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="input-full-name" required />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="eyebrow text-[#5C5C5C]">Email *</label>
              <input className="ee-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="input-email" required />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="eyebrow text-[#5C5C5C]">Phone</label>
              <input className="ee-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="input-phone" />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="eyebrow text-[#5C5C5C]">Guests *</label>
              <input className="ee-input" type="number" min="1" max="20" value={form.guests} onChange={(e) => set("guests", e.target.value)} data-testid="input-guests" required />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="eyebrow text-[#5C5C5C]">Destination *</label>
              <select className="ee-input bg-transparent" value={form.destination} onChange={(e) => set("destination", e.target.value)} data-testid="select-destination" required>
                <option value="">Select a resort</option>
                {destinations.map((d) => (
                  <option key={d.id} value={d.name}>{`${d.name} · ${d.country}`}</option>
                ))}
                <option value="Unsure — recommend for me">Unsure — recommend for me</option>
              </select>
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="eyebrow text-[#5C5C5C]">Trip Type *</label>
              <select className="ee-input bg-transparent" value={form.trip_type} onChange={(e) => set("trip_type", e.target.value)} data-testid="select-trip-type" required>
                <option value="">Select</option>
                {tripTypes.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            <div className="col-span-2 sm:col-span-1">
              <label className="eyebrow text-[#5C5C5C]">Start Date *</label>
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" className="ee-input flex items-center justify-between text-left" data-testid="btn-start-date">
                    <span className={start ? "text-[#1A1A1A]" : "text-[#1A1A1A]/40"}>
                      {start ? format(start, "PPP") : "Pick a date"}
                    </span>
                    <CalendarIcon size={16} strokeWidth={1.25} className="text-[#5C5C5C]" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={start} onSelect={setStart} initialFocus /></PopoverContent>
              </Popover>
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="eyebrow text-[#5C5C5C]">End Date *</label>
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" className="ee-input flex items-center justify-between text-left" data-testid="btn-end-date">
                    <span className={end ? "text-[#1A1A1A]" : "text-[#1A1A1A]/40"}>
                      {end ? format(end, "PPP") : "Pick a date"}
                    </span>
                    <CalendarIcon size={16} strokeWidth={1.25} className="text-[#5C5C5C]" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={end} onSelect={setEnd} initialFocus /></PopoverContent>
              </Popover>
            </div>

            <div className="col-span-2">
              <label className="eyebrow text-[#5C5C5C]">Budget *</label>
              <select className="ee-input bg-transparent" value={form.budget} onChange={(e) => set("budget", e.target.value)} data-testid="select-budget" required>
                <option value="">Select a range</option>
                {budgets.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>

            <div className="col-span-2">
              <label className="eyebrow text-[#5C5C5C]">Anything else?</label>
              <textarea className="ee-input" rows={3} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="input-message"
                placeholder="Celebrating something? Dietary needs? Special requests." />
            </div>

            <div className="col-span-2 pt-6 flex flex-col sm:flex-row sm:items-center gap-4">
              <button type="submit" disabled={submitting} className="ee-btn-primary" data-testid="btn-submit-inquiry">
                {submitting ? "Sending..." : "Send Inquiry"}
                {!submitting && <ArrowUpRight size={14} strokeWidth={1.5} />}
              </button>
              <p className="text-xs text-[#5C5C5C]">By sending you agree to be contacted by our concierge.</p>
            </div>
            <div className="col-span-2 mt-4" data-testid="book-now-pay-later">
              <p className="eyebrow text-[#C5A059] mb-1">Book Now · Pay Later</p>
              <p className="text-sm text-[#1A1A1A]/70 leading-relaxed">
                Flexible payment options to fit your lifestyle.
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
